import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

const POLLING_CHECK_INTERVAL = 5 * 60 * 1000;

const STATUSES_TO_POLL = ['created', 'processing', 'pending'];

const FINAL_STATUSES = ['settled', 'returned', 'voided'];

const SIX_HOURS = 6 * 60 * 60 * 1000;
const TWELVE_HOURS = 12 * 60 * 60 * 1000;
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
const FOUR_DAYS = 4 * 24 * 60 * 60 * 1000;

function getNextCheckInterval(transactionAge: number): number {
  if (transactionAge < TWENTY_FOUR_HOURS) {
    return SIX_HOURS;
  } else if (transactionAge < FOUR_DAYS) {
    return TWELVE_HOURS;
  } else {
    return TWENTY_FOUR_HOURS;
  }
}

function shouldCheckTransaction(
  initiatedAt: string,
  lastCheckedAt: string | null
): boolean {
  const now = Date.now();
  const initiated = new Date(initiatedAt).getTime();
  const lastChecked = lastCheckedAt ? new Date(lastCheckedAt).getTime() : initiated;

  const transactionAge = now - initiated;
  const timeSinceLastCheck = now - lastChecked;

  const requiredInterval = getNextCheckInterval(transactionAge);

  return timeSinceLastCheck >= requiredInterval;
}

export function useACHPolling() {
  const [isPolling, setIsPolling] = useState(false);
  const [isCheckingNow, setIsCheckingNow] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [activeTransactionCount, setActiveTransactionCount] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkTransactionStatus = async (transactionId: string) => {
    try {
      console.log(`[ACH Status Check] Starting check for transaction: ${transactionId}`);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('No session available for ACH status check');
        return null;
      }

      console.log(`[ACH Status Check] Calling edge function for transaction: ${transactionId}`);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-ach-status`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            achTransactionId: transactionId,
          }),
        }
      );

      console.log(`[ACH Status Check] Response status: ${response.status} for transaction: ${transactionId}`);

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`[ACH Status Check] Failed for transaction ${transactionId}:`, errorData);
        return null;
      }

      const result = await response.json();
      console.log(`[ACH Status Check] Result for transaction ${transactionId}:`, result);

      await supabase
        .from('ach_transactions')
        .update({ last_checked_at: new Date().toISOString() })
        .eq('id', transactionId);

      console.log(`[ACH Status Check] ✓ Updated transaction ${transactionId} - New status: ${result.status}`);
      return result;
    } catch (error) {
      console.error(`[ACH Status Check] Error checking transaction ${transactionId}:`, error);
      return null;
    }
  };

  const pollTransactions = async () => {
    try {
      console.log('[ACH Polling] Starting poll cycle...');

      const { data: transactions, error } = await supabase
        .from('ach_transactions')
        .select('id, status, transaction_id, payroll_entry_id, initiated_at, last_checked_at')
        .in('status', STATUSES_TO_POLL);

      if (error) {
        console.error('[ACH Polling] Error fetching ACH transactions:', error);
        return;
      }

      if (!transactions || transactions.length === 0) {
        console.log('[ACH Polling] No active ACH transactions found. This means either:');
        console.log('  1. All ACH transactions have reached a final status (settled/returned/voided), OR');
        console.log('  2. No ACH payments have been initiated yet.');
        console.log('  → To process ACH payments, go to Payroll History and click "Process ACH Payment"');
        setActiveTransactionCount(0);
        return;
      }

      console.log(`[ACH Polling] Found ${transactions.length} transaction(s) with active status:`, transactions.map(t => ({
        id: t.id,
        status: t.status,
        transaction_id: t.transaction_id
      })));

      const transactionsDueForCheck = transactions.filter(t =>
        shouldCheckTransaction(t.initiated_at, t.last_checked_at)
      );

      setActiveTransactionCount(transactions.length);

      if (transactionsDueForCheck.length === 0) {
        console.log(
          `[ACH Polling] Found ${transactions.length} active transaction(s), but none are due for status check yet`
        );
        return;
      }

      console.log(
        `[ACH Polling] Checking ${transactionsDueForCheck.length} of ${transactions.length} transaction(s) due for status update...`
      );

      for (const transaction of transactionsDueForCheck) {
        console.log(`[ACH Polling] Checking transaction ${transaction.id} (Valmar ID: ${transaction.transaction_id})`);
        const result = await checkTransactionStatus(transaction.id);

        if (result && FINAL_STATUSES.includes(result.status.toLowerCase())) {
          console.log(`[ACH Polling] ✓ Transaction ${transaction.id} reached final status: ${result.status}`);
        }

        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      setLastUpdate(new Date());
      console.log('[ACH Polling] Poll cycle completed successfully');
    } catch (error) {
      console.error('[ACH Polling] Error in polling cycle:', error);
    }
  };

  const pollNow = async () => {
    setIsCheckingNow(true);
    await pollTransactions();
    setIsCheckingNow(false);
  };

  const startPolling = () => {
    if (intervalRef.current) {
      return;
    }

    setIsPolling(true);
    pollTransactions();

    intervalRef.current = setInterval(() => {
      pollTransactions();
    }, POLLING_CHECK_INTERVAL);

    console.log('ACH smart polling started (checks every 5 minutes for due transactions)');
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
    console.log('ACH polling stopped');
  };

  useEffect(() => {
    startPolling();

    return () => {
      stopPolling();
    };
  }, []);

  const scheduleInitialCheck = () => {
    console.log('Scheduling initial ACH status check in 2 seconds...');
    setTimeout(() => {
      console.log('Running initial ACH status check...');
      pollTransactions();
    }, 2000);
  };

  return {
    isPolling,
    isCheckingNow,
    lastUpdate,
    activeTransactionCount,
    startPolling,
    stopPolling,
    pollNow,
    scheduleInitialCheck,
  };
}
